
import { Component, inject, OnInit } from '@angular/core';
import {
  FormArray,
  FormBuilder,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  Validators
} from "@angular/forms";
import { Router } from "@angular/router";
import { defaultUser } from '@local-mocks';
import { Users as UsersTypes } from '@types-lib';
import { SnackbarClassType, SnackbarMessage, SnackNotificationsDialogService } from '@ui-lib/services';
import { ENVIRONMENT, PIMSEnvironment } from "environments";
import { catchError, EMPTY, takeUntil, tap } from "rxjs";
import { Notifications } from "../../../../../angularJs/src/app/services/notification.service";
import { GENDERS, ROLES, USER_TITLES, Users } from "../../../../../angularJs/src/app/services/user.service";
import Utils from "../../../../../angularJs/src/app/services/utils.service";
import { UsersApiService } from "../../../api/users/users.service";
import { Unsubscribe } from "../../../core/unsubscribe/unsubscribe";

@Component({
    selector: 'new-user',
    imports: [FormsModule, ReactiveFormsModule],
    templateUrl: './new-user.component.html',
    styleUrl: './new-user.component.scss'
})
export class NewUserComponent extends Unsubscribe implements OnInit{
	private snackBar = inject(SnackNotificationsDialogService);
	private Notifications = inject(Notifications);
	private Users = inject(Users);
	private router = inject(Router);
	private formBuilder = inject(FormBuilder);
	private Utils = inject(Utils);
	private userApiService = inject(UsersApiService);

	private env = inject<PIMSEnvironment>(ENVIRONMENT)
	user: UsersTypes.User = defaultUser
	userRoles: UsersTypes.Roles = ROLES
	genders: string[] = GENDERS
	userTitles:string[] = USER_TITLES
	creating = false
	rolesArray: UsersTypes.Role[] = []
	userForm: FormGroup;

	constructor() {
		super()
		this.userForm = this.formBuilder.group({
			title: ['', Validators.required],
			firstname: ['', Validators.required],
			lastname: ['', Validators.required],
			initials: ['', Validators.required],
			email: ['', [Validators.required, Validators.email]],
			credentials: [''],
			password: ['', Validators.required],
			dob: ['', Validators.required],
			gender: ['', Validators.required],
			security: this.formBuilder.group({
				question: ['', Validators.required],
				answer: ['', Validators.required]
			}),
			phones: this.formBuilder.group({
				0: this.formBuilder.group({
					number: ['', Validators.required]
				})
			}),
			address: this.formBuilder.group({
				road: ['', Validators.required],
				city: ['', Validators.required],
				state: ['', Validators.required],
				zip: ['', Validators.required]
			}),
			roles: this.formBuilder.array([])
		});
	}

	ngOnInit() {
		// System role should no longer be assigned to a new user BOD-2615
		const acceptableRoles = Object.values(ROLES).filter(
			(role) => role.ID !== ROLES.SYSTEM.ID
		);
		for (const acceptableRole of acceptableRoles) {
			this.rolesArray.push(acceptableRole)
		}
	}

	get roles() {
		return this.userForm.get('roles') as FormArray
	}

	addRole(): void {
		for (const key in this.userRoles) {
			this.roles.push(this.formBuilder.control(this.userRoles[key].NAME))
		}
	}

	updateRoles(event:any) {
		const rolesArray: FormArray = this.userForm.get('roles') as FormArray
		this.Utils.updateFormArray(event, rolesArray)
	}

	createUser() {
		this.userForm.markAllAsTouched();
		const formValues = this.userForm.value;
		const email = this.userForm.get('email')?.value
		if (this.userForm.invalid || !this.Utils.isValidEmail(email)) {
			const errors = this.Utils.getAllErrors(this.userForm)
			if (errors !== null) {
				const errorsArray = Object.keys(errors)
				const message = errorsArray.length < 2 && errorsArray.includes('email') ? this.Notifications.users.invalidEmail : this.Notifications.users.invalidForm
				this.snackBar.openErrorMessage(message)
				return
			}
		}
		this.creating = true
		this.userApiService.createNewUser(formValues)
			.pipe(
				takeUntil(this.unsubscribe$),
				tap(() => {
					this.handleSuccess()
				}),
				catchError((error: any) => {
					if (error.status === 409) {
						this.snackBar.openErrorMessage(this.Notifications.users.emailInUse)
					} else {
						if (Array.isArray(error.error.message.summary)) {
							const messages: SnackbarMessage[] = error.error.message.summary.map((message: string) => ({
								text: message,
								type: SnackbarClassType.ERROR
							}));
							this.snackBar.addMessagesToQueue(messages)
						} else {
							this.snackBar.openErrorMessage((this.Notifications.generic.failure))
						}
					}
					this.creating = false
					return EMPTY
				})
			)
			.subscribe()
	}

	handleSuccess() {
		this.creating = false
		this.snackBar.openSuccessMessage(this.Notifications.users.createSuccess)
		this.router.navigate(['/users'])
	}
}
